import { Controller, useForm } from "react-hook-form";
import { Link, useHistory, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { AcceptedAgeRange } from "@/portals/(shared)/(age-range)/AcceptedAgeRange";
import { adminRoute } from "@/config/roles";
import type { AgeRange, ProviderType, UUID } from "@/core/types";
import { Button2 } from "@/components/UI2/atoms/Button";
import { Checkbox2 } from "@/components/UI2/atoms/Checkbox";
import { CredentialsMultiSelect } from "@/portals/(shared)/components/CredentialsMultiSelect";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { FieldError2 } from "@/components/UI2/atoms/FieldError";
import { MultiSelect2 } from "@/components/UI2/molecules/MultiSelect";
import { ProviderEnum } from "@/core/constants";
import { Select2 } from "@/components/UI2/molecules/Select";
import { showToast } from "@/providers/ToastProvider";
import { STATES } from "@/constants/states";
import { TextField2 } from "@/components/UI2/molecules/TextField";

import { adaptIntakePracticeOptions } from "./helpers/adapters";
import {
  NEW_PROVIDER_DEFAULT_VALUES,
  newProviderSchema,
  type NewProviderFormValues,
} from "./schema/newProvider";
import { useCreateProvider } from "./api/mutations/useCreateProvider";
import { usePractice } from "./api/queries/usePractice";
import { usePracticeList } from "./api/queries/usePracticeList";

export function NewProvider() {
  const { practiceId } = useParams<{ practiceId: UUID }>();
  const history = useHistory();

  const [acceptedAgeRanges, setAcceptedAgeRanges] = useState<AgeRange[]>([{ max: 200, min: 18 }]);

  const { control, formState, handleSubmit, reset, resetField, watch } =
    useForm<NewProviderFormValues>({
      defaultValues: NEW_PROVIDER_DEFAULT_VALUES,
      mode: "onChange",
      resolver: zodResolver(newProviderSchema),
    });

  const providerType = watch("provider_type");
  const isAcceptNewPatientsVisible = showsAcceptNewPatients(providerType);
  const isAcceptedAgeRangesVisible = showsAcceptedAgeRanges(providerType);

  // Reset cp_credentials on provider type change — clears value, errors, and touched state,
  // so the field starts fresh without triggering premature validation.
  useEffect(() => {
    resetField("cp_credentials");
  }, [providerType, resetField]);

  const { data: practice } = usePractice({ practiceId });

  const { data: intakePracticeOptions = [] } = usePracticeList({
    select: adaptIntakePracticeOptions,
  });

  useEffect(() => {
    if (!practice) return;

    reset({
      ...NEW_PROVIDER_DEFAULT_VALUES,
      address: practice.address,
      practice: practice.name,
    });
  }, [practice, reset]);

  const { isPending, mutate: createProvider } = useCreateProvider({
    onError: () =>
      showToast([{ heading: "Failed to create provider. Please try again.", variant: "error" }]),
    onSuccess: () => {
      showToast([{ heading: "Provider created successfully." }]);
      history.push(`${adminRoute}/practice/${practiceId}`);
    },
  });

  function onSubmit(values: NewProviderFormValues) {
    createProvider({
      accept_new_patients: values.accept_new_patients,
      address: values.address,
      cp_credentials: values.cp_credentials.join(", "),
      group_type: "provider",
      intake_practice_ids: values.intake_practice_ids as UUID[],
      mobile: values.mobile || "None",
      name: values.name,
      npi_number: values.npi_number,
      practice: values.practice,
      practice_id: practiceId,
      practice_ids: [practiceId],
      provider_type: values.provider_type,
      Username: values.Username,
      ...(isAcceptedAgeRangesVisible && { accepted_age_range: acceptedAgeRanges }),
    });
  }

  if (!practice) return null;

  return (
    <div>
      {/** TODO: Refactor to Breadcrumbs */}
      <div className="survey-back-link-wpr tw:mx-auto tw:my-3 tw:py-2 tw:text-sm">
        <Link to={`${adminRoute}/practice`}>
          <p className="tw:ml-0 tw:inline">Practices</p>
        </Link>
        <Link to={`${adminRoute}/practice/${practiceId}`}>
          <p className="tw:ml-0 tw:inline"> {">"} Practice Details</p>
        </Link>
        <span> {">"} Add Provider</span>
      </div>

      <DashboardCard2>
        <form
          className="tw:grid tw:w-full tw:grid-cols-1 tw:gap-4 tw:lg:grid-cols-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isRequired
                label="Name"
                mode="input"
                onChange={field.onChange}
                placeholder="Name"
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="Username"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isRequired
                label="Email"
                mode="input"
                onChange={field.onChange}
                placeholder="Email"
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="practice"
            render={({ field }) => (
              <TextField2
                hasStrongLabel
                isReadOnly
                label="Practice"
                mode="input"
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />

          <CredentialsMultiSelect control={control} providerType={providerType} />

          <Controller
            control={control}
            name="npi_number"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isRequired={providerType !== "CARE_MANAGER"}
                label="NPI Number"
                mode="input"
                onChange={field.onChange}
                placeholder="NPI Number"
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="mobile"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                label="Mobile"
                mode="input"
                onChange={field.onChange}
                placeholder="Mobile"
                value={field.value ?? ""}
              />
            )}
          />

          <Controller
            control={control}
            name="provider_type"
            render={({ field, fieldState }) => (
              <Select2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isRequired
                label="Provider Type"
                onChange={field.onChange}
                options={PROVIDER_TYPE_OPTIONS}
                placeholder="Select provider type"
                value={field.value}
              />
            )}
          />

          {isAcceptNewPatientsVisible && (
            <div className="tw:flex tw:items-center tw:gap-2 tw:lg:mt-5">
              <Controller
                control={control}
                name="accept_new_patients"
                render={({ field, fieldState }) => (
                  <>
                    <Checkbox2
                      isInvalid={!!fieldState.error}
                      isSelected={field.value}
                      onChange={field.onChange}
                    >
                      Receive New Patients
                    </Checkbox2>
                    {fieldState.error && <FieldError2>{fieldState.error.message}</FieldError2>}
                  </>
                )}
              />
            </div>
          )}

          <Controller
            control={control}
            name="address.street"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isRequired
                label="Street"
                mode="input"
                onChange={field.onChange}
                placeholder="Enter Street"
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="address.city"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isRequired
                label="City"
                mode="input"
                onChange={field.onChange}
                placeholder="Enter City"
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="address.state"
            render={({ field, fieldState }) => (
              <Select2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isRequired
                label="State"
                onChange={(key) => field.onChange(key)}
                options={STATE_OPTIONS}
                placeholder="Enter State"
                value={field.value}
              />
            )}
          />

          <Controller
            control={control}
            name="address.zip_code"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isRequired
                label="Zip"
                mode="input"
                onChange={field.onChange}
                placeholder="Enter zip"
                value={field.value}
              />
            )}
          />

          {providerType === "BEHAVIORAL_INTAKE" && (
            <Controller
              control={control}
              name="intake_practice_ids"
              render={({ field, fieldState }) => (
                <MultiSelect2
                  errorMessage={fieldState.error?.message}
                  hasStrongLabel
                  isInvalid={!!fieldState.error}
                  label="Intake Coordinator For Practice(s)"
                  onChange={(keys) => field.onChange(keys.map(String))}
                  options={intakePracticeOptions}
                  placeholder="Intake Coordinator For Practice(s)"
                  value={field.value ?? []}
                />
              )}
            />
          )}

          {isAcceptedAgeRangesVisible && (
            <AcceptedAgeRange
              acceptedAgeRanges={acceptedAgeRanges}
              setAcceptedAgeRanges={setAcceptedAgeRanges}
            />
          )}

          <div className="tw:col-span-2 tw:mt-6 tw:flex tw:justify-center tw:gap-3 tw:lg:justify-end">
            <Button2
              onPress={() => history.push(`${adminRoute}/practice`)}
              type="button"
              variant="secondary"
            >
              Cancel
            </Button2>
            <Button2
              isDisabled={!formState.isValid}
              isLoading={isPending}
              labelLoading={null}
              type="submit"
              variant="primary"
            >
              Add
            </Button2>
          </div>
        </form>
      </DashboardCard2>
    </div>
  );
}

const PROVIDER_TYPE_OPTIONS: { id: ProviderType; name: string }[] = [
  { id: ProviderEnum.PCP, name: "Primary Care Provider" },
  { id: ProviderEnum.CARE_MANAGER, name: "Care Manager" },
  { id: ProviderEnum.BEHAVIORAL, name: "Behavioral Specialist" },
  { id: ProviderEnum.BEHAVIORAL_INTAKE, name: "Intake Behavioral Specialist" },
  { id: ProviderEnum.PRESCRIBE, name: "Prescriber" },
  { id: ProviderEnum.PEER, name: "Peer" },
  { id: ProviderEnum.CASE_MANAGER, name: "Case Manager" },
];

const STATE_OPTIONS = STATES.map(({ display, value }) => ({ id: value, name: display }));

function showsAcceptNewPatients(providerType: ProviderType) {
  return (
    providerType?.startsWith(ProviderEnum.BEHAVIORAL) === true ||
    providerType === ProviderEnum.PEER ||
    providerType === ProviderEnum.CASE_MANAGER
  );
}

function showsAcceptedAgeRanges(providerType: ProviderType) {
  return (
    providerType?.startsWith(ProviderEnum.BEHAVIORAL) === true ||
    providerType?.startsWith(ProviderEnum.PRESCRIBE) === true ||
    providerType === ProviderEnum.PEER ||
    providerType === ProviderEnum.CASE_MANAGER
  );
}
